import { useEffect, useState } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { getCreditInstrumentByFiling } from '../../api/credit'
import { getTransferByInstrument, listBrokers, listBuyers } from '../../api/monetization'
import { ApiError } from '../../api/problem'
import { useProgramRefs } from '../../lib/useProgramRefs'
import type { CreditInstrumentView } from '../../types/credit'
import type { TransferView } from '../../types/monetization'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'

export interface MonetizationPanelProps {
  filings: ProgramFilingDetailView[]
}

interface MonetizationRow {
  filing: ProgramFilingDetailView
  instrument: CreditInstrumentView
  transfer: TransferView
  buyerName: string | null
  brokerName: string | null
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

function formatDiscountRate(discount: number): string {
  // transfer_txn.discount is a RATE (e.g. 0.0800), not a dollar amount (docs/data-dictionary.md
  // §12.5, V15 migration) — render as a percentage, never re-derived arithmetic.
  return `${(discount * 100).toFixed(2)}%`
}

const INSTRUMENT_STATUS_VARIANT: Record<string, StatusPillVariant> = {
  certified: 'success',
  transferred: 'info',
}

/**
 * The Case-workspace Monetization tab (GA demo v4, film cases only; enriched by the
 * case-enrichment story to lay out the full transfer economics) — the `TransferTxn` for each
 * certified filing's `CreditInstrument` (`GET /credit-instruments/{id}/transfer`, wired onto the
 * already-published `TransferReadApi.findByInstrumentId` port, see `TransferController`), alongside
 * the `CreditInstrument` itself (gross certified credit + its current lifecycle `status`, both
 * already fetched to resolve the transfer in the first place — no extra request). Buyer and Broker
 * names are resolved via `GET /buyers`/`GET /brokers` (unpaginated arrays, real endpoints already
 * published for `module.monetization`'s Buyer/Broker registries) rather than rendering raw UUIDs
 * (CLAUDE.md §4 IA). "Discount amount" (`amount - salePrice`) is a plain display arithmetic over
 * two non-guarded fields (R-IB19-1) — never a re-derivation of a `@GuardedField`, never written
 * back. A filing with no CreditInstrument, or a CreditInstrument not yet monetized, contributes no
 * row — never a fabricated placeholder.
 */
export function MonetizationPanel({ filings }: MonetizationPanelProps) {
  const [rows, setRows] = useState<MonetizationRow[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const programRefs = useProgramRefs(filings.map((f) => f.programId))

  useEffect(() => {
    let cancelled = false
    setState('loading')
    setError(null)

    async function load() {
      try {
        const [buyers, brokers] = await Promise.all([
          listBuyers().catch(() => []),
          listBrokers().catch(() => []),
        ])
        const buyerNameById = new Map(buyers.map((b) => [b.id, b.name]))
        const brokerNameById = new Map(brokers.map((b) => [b.id, b.name]))

        const settled = await Promise.all(
          filings.map(async (filing) => {
            const instrument = await getCreditInstrumentByFiling(filing.id)
            if (!instrument) return null
            const transfer = await getTransferByInstrument(instrument.id)
            if (!transfer) return null
            const row: MonetizationRow = {
              filing,
              instrument,
              transfer,
              buyerName: buyerNameById.get(transfer.buyerId) ?? null,
              brokerName: transfer.brokerId ? (brokerNameById.get(transfer.brokerId) ?? null) : null,
            }
            return row
          }),
        )
        if (!cancelled) {
          setRows(settled.filter((r): r is MonetizationRow => r !== null))
          setState('ready')
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load transfers.')
          setState('error')
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [filings])

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading transfers…</p>
  }

  if (state === 'error') {
    return <InlineAlert severity="error">{error}</InlineAlert>
  }

  if (rows.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>No credit on this case has been transferred (monetized) yet.</p>
  }

  return (
    <table className="iw-table" aria-label="Credit transfers">
      <thead>
        <tr>
          <th scope="col">Program</th>
          <th scope="col">Buyer</th>
          <th scope="col">Broker</th>
          <th scope="col">Gross credit</th>
          <th scope="col">Discount rate</th>
          <th scope="col">Discount amount</th>
          <th scope="col">Net proceeds</th>
          <th scope="col">Status</th>
          <th scope="col">Transfer date</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ filing, instrument, transfer, buyerName, brokerName }) => {
          const discountAmount = transfer.amount - transfer.salePrice
          return (
            <tr key={transfer.id}>
              <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                <EntityRef kind="program" id={filing.programId} name={programRefs.get(filing.programId)?.display} />
              </td>
              <td>
                <EntityRef kind="buyer" id={transfer.buyerId} name={buyerName} />
              </td>
              <td>
                {transfer.brokerId ? (
                  <EntityRef kind="broker" id={transfer.brokerId} name={brokerName} />
                ) : (
                  'Direct (no broker)'
                )}
              </td>
              <td>{formatAmount(instrument.certifiedAmount, instrument.currency)}</td>
              <td>{formatDiscountRate(transfer.discount)}</td>
              <td>{formatAmount(discountAmount, instrument.currency)}</td>
              <td>{formatAmount(transfer.salePrice, instrument.currency)}</td>
              <td>
                <StatusPill variant={INSTRUMENT_STATUS_VARIANT[instrument.status] ?? 'neutral'} label={instrument.status} />
              </td>
              <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                {transfer.transferDate}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
